import React, {useEffect, useState} from 'react';
import {InvestorGroupType, MemberGroup} from "../../../ClientManagement/models/InvestorGroupType";
import {assetsApiClient} from "../../../Assets/AssetsApiClient";
import {clientManagementApiClient} from "../../../ClientManagement/ClientManagementApiClient";
import {resetClientAssets, selectClientAssets, setClientAssets,} from "../../../Assets/clientAssetsSlice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {LegalEntityReadModel} from "../../../Assets/models/Ownership";
import {CurrentNetWorthReport} from '../CurrentNetWorthReport/CurrentNetWorthReport';
import AssetSummaryReport from '../AssetSummaryDetailReport/AssetSummaryReport';
import {ReportConfig} from 'src/Reporting/models/Reporting';
import {ProfileResponse} from "../../../ClientManagement/models/ProfileResponse";

type AssetsReportsProps = {
    reportConfig: ReportConfig,
    profile: ProfileResponse,
    approvedProfile: ProfileResponse
}
const AssetsReports = ({reportConfig, profile, approvedProfile}: AssetsReportsProps) => {
    const dispatch = useAppDispatch();
    const [memberGroup, setMemberGroup] = useState<MemberGroup>();
    const [legalEntities, setLegalEntities] = useState<LegalEntityReadModel[]>();
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    const [investorGroup, setInvestorGroup] = useState<InvestorGroupType>();

    const clientAssets = useAppSelector(selectClientAssets);

    const activeProfileId = profile?.id;
    const displayName = approvedProfile.displayName;

    useEffect(() => {

        if (activeProfileId && !isDataLoaded) {
            Promise.all([
                assetsApiClient.getAssetsSummary(activeProfileId),
                clientManagementApiClient.getInvestorGroup(activeProfileId),
                assetsApiClient.getLegalEntities(activeProfileId),
                clientManagementApiClient.getMemberGroup(activeProfileId)
            ])
                .then(([assetsResponse, investorGroupResponse, legalEntitiesResponse, memberGroupResponse]) => {
                    dispatch(setClientAssets(assetsResponse));
                    setInvestorGroup(investorGroupResponse);
                    setLegalEntities(legalEntitiesResponse);
                    setMemberGroup({
                        ...memberGroupResponse,
                        additionalMembers: [...memberGroupResponse.additionalMembers]
                    });
                    setIsDataLoaded(true);

                })
                .catch(error => console.error('Could not fetch asset summary data', error.message))
        }
    }, [approvedProfile]);

    useEffect(() => {
        return function cleanup() {
            dispatch(resetClientAssets());
        }
    }, []);

    if (!isDataLoaded || !(clientAssets && investorGroup && legalEntities && memberGroup?.primaryMember)) {
        return <LoadingIndicator/>;
    }

    return <>
        {reportConfig.assetsSummary.isEnabled && <AssetSummaryReport
            isDetailed={false}
            displayName={displayName}
            investorGroup={investorGroup!}
            profileId={profile.id}
            memberGroup={memberGroup}
            legalEntities={legalEntities!}
        />}
        {reportConfig.assetsSummaryDetailed.isEnabled && <AssetSummaryReport
            isDetailed={true}
            displayName={displayName}
            investorGroup={investorGroup!}
            profileId={profile.id}
            memberGroup={memberGroup}
            legalEntities={legalEntities!}
        />}
        {reportConfig.currentNetWorth.isEnabled && <CurrentNetWorthReport
            showDeathBenefit={false}
            isDetailed={false}
            displayName={displayName}
            investorGroup={investorGroup!}
            legalEntities={legalEntities!}
            profile={profile}
        />}
        {reportConfig.currentNetWorthDetailed.isEnabled && <CurrentNetWorthReport
            showDeathBenefit={false}
            isDetailed={true}
            displayName={displayName}
            investorGroup={investorGroup!}
            legalEntities={legalEntities!}
            profile={profile}
        />}
        {reportConfig.currentNetWorthAtDeath.isEnabled && <CurrentNetWorthReport
            showDeathBenefit={true}
            isDetailed={false}
            displayName={displayName}
            investorGroup={investorGroup!}
            legalEntities={legalEntities!}
            profile={profile}
        />}
        {reportConfig.currentNetWorthAtDeathDetailed.isEnabled && <CurrentNetWorthReport
            showDeathBenefit={true}
            isDetailed={true}
            displayName={displayName}
            investorGroup={investorGroup!}
            legalEntities={legalEntities!}
            profile={profile}
        />}
    </>
}

export default AssetsReports;
